import {
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import { intakeApiClient } from '@/services/api/intakeService';
import type { Intake } from '@/services/types/intake-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { FirmLink } from './FirmLink';

/**
 * Intake tooltip content component
 * Pure presentation component - memoized for performance
 */
export const IntakeTooltipContent = memo<EntityTooltipContentProps<Intake>>(
  ({ fetchState }) => {
    // Compute derived values before early returns (Hooks must be called unconditionally)
    const intake = fetchState.status === 'success' ? fetchState.data : null;
    const createdAt = intake?.created_at;

    const formattedDate = useMemo(() => {
      if (!createdAt) return null;
      return new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }, [createdAt]);

    // Handle loading state
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    // Handle error state
    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="intake" />;
    }

    // Handle idle or empty state - no data yet
    if (fetchState.status === 'idle' || !intake) {
      return <EmptyState entityType="intake" />;
    }

    // Success state - extract data
    const { form_data, firm, firm_name, status } = intake;
    const clientName = form_data?.personal_info?.full_name || 'Intake';

    const statusBadge = status && createStatusBadge({ value: status });

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={ClipboardDocumentListIcon} title={clientName} />

        <TooltipSection withBorder>
          {firm && firm_name && (
            <InfoRow
              icon={BuildingOffice2Icon}
              label="Firm"
              value={
                <FirmLink entityId={firm} side="left" className="text-xs">
                  {firm_name}
                </FirmLink>
              }
            />
          )}
          {status && (
            <InfoRow
              icon={UserIcon}
              label="Status"
              value={statusBadge}
              disableCopy
            />
          )}
          <InfoRow
            icon={ClipboardDocumentListIcon}
            label="Created"
            value={formattedDate}
          />
        </TooltipSection>
      </div>
    );
  },
);

IntakeTooltipContent.displayName = 'IntakeTooltipContent';

/**
 * Hoverable intake link with tooltip preview
 *
 * Shows intake client name, firm (with nested link), status, and created date on hover.
 * Supports nested tooltips for related entities like firms.
 * Click to navigate to intake detail page.
 *
 * @example
 * ```tsx
 * <IntakeLink entityId={intakeId}>John Smith Intake</IntakeLink>
 * ```
 */
export const IntakeLink = createEntityLink<Intake, string>({
  tooltipContent: IntakeTooltipContent,
  getDetailUrl: (id) => `/intakes/${id}`,
  useQuery: intakeApiClient.endpoints.getIntake.useQuery,
  getQueryArg: (id) => id,
  displayName: 'IntakeLink',
});
